import { ReactElement, useMemo, useState } from 'react';
import classNames from 'classnames';
import { NavLink, useLocation } from 'react-router-dom';
import { Box, IconButton, Menu, MenuItem, styled } from '@mui/material';
import { ExpandMore } from '@mui/icons-material';

import { StyledTabs } from '../../layout/Headers/utils/styled-components';

import { TabButton } from '..';

import { useAvailableMenuWidth } from './utils';

export type TopMenuLinkType = {
  href: string;
  label: string;
  isActive?: boolean;
  /** Wraps the rendered tab button, e.g. to attach a badge or tooltip to a specific tab */
  renderButton?: (button: ReactElement) => ReactElement;
};

export type BaseTopMenuProps = {
  links?: TopMenuLinkType[];
};

const StyledTabButton = styled(TabButton)(({ theme }) => ({
  paddingTop: theme.spacing(0.5),
  paddingBottom: theme.spacing(0.5),
}));

export const BaseTopMenu = ({ links }: BaseTopMenuProps): ReactElement | null => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const location = useLocation();

  const handleMoreClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const availableWidth = useAvailableMenuWidth('.base-header-container', 'top-menu-bar');
  const averageTabWidth = 130;

  const maxVisibleLinks = Math.max(1, Math.floor(availableWidth / averageTabWidth));

  // Enhance links with isActive if not set
  const linksWithIsActive = useMemo(() => {
    return links?.map((link) => ({
      ...link,
      isActive: link.isActive ?? location.pathname === link.href,
    }));
  }, [links, location.pathname]);

  const { visibleLinks, dropdownLinks } = useMemo(() => {
    const fixedFirst = linksWithIsActive?.[0]; // Always show first tab
    const dynamicVisible = linksWithIsActive?.slice(1, maxVisibleLinks);
    const dynamicDropdown = linksWithIsActive?.slice(maxVisibleLinks);

    return {
      visibleLinks: [fixedFirst, ...(dynamicVisible || [])].filter((item) => !!item),
      dropdownLinks: dynamicDropdown,
    };
  }, [linksWithIsActive, maxVisibleLinks]);

  if (!visibleLinks.length) return null;

  return (
    <Box display="flex" alignItems="center" className="top-menu-bar">
      <StyledTabs>
        <ul style={{ margin: 0 }}>
          {visibleLinks.map((link) => {
            const button = (
              <StyledTabButton component={NavLink} to={link.href} end color="inherit" isActive={link.isActive}>
                {link.label}
              </StyledTabButton>
            );

            return (
              <li key={link.href} className={classNames({ active: link.isActive })}>
                {link.renderButton ? link.renderButton(button) : button}
              </li>
            );
          })}
        </ul>
      </StyledTabs>

      {dropdownLinks && dropdownLinks.length > 0 && (
        <>
          <IconButton size="small" onClick={handleMoreClick}>
            <ExpandMore />
          </IconButton>
          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
            {dropdownLinks?.map((link) => (
              <MenuItem
                key={link.href}
                onClick={handleClose}
                component={NavLink}
                to={link.href}
                selected={link.isActive}
              >
                {link.label}
              </MenuItem>
            ))}
          </Menu>
        </>
      )}
    </Box>
  );
};
